import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 btn-glass-hover relative overflow-hidden transition-all duration-300",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80 hover:shadow-[0_6px_24px_hsl(var(--primary)/0.35)] [&_svg]:hover:scale-110 [&_svg]:transition-transform",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80 hover:shadow-[0_6px_24px_hsl(var(--destructive)/0.3)] [&_svg]:hover:scale-110 [&_svg]:transition-transform",
        outline: "border border-input bg-background hover:bg-primary/5 hover:border-primary/30 hover:text-primary hover:shadow-[0_4px_16px_hsl(var(--primary)/0.12)] [&_svg]:hover:scale-110 [&_svg]:transition-transform",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70 hover:shadow-[0_4px_16px_hsl(var(--foreground)/0.08)] [&_svg]:hover:scale-110 [&_svg]:transition-transform",
        ghost: "hover:bg-primary/5 hover:text-primary [&_svg]:hover:scale-110 [&_svg]:transition-transform",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
